import type { TranslationProgressEntry, TranslationProgressMap } from '../types'
import { splitLanguagePair } from './languagePairs'

const LANGUAGE_NAME_ALIASES: Record<string, string> = {
  'pt-br': 'Brazilian Portuguese',
  'pt_br': 'Brazilian Portuguese',
  'brazilian portuguese': 'Brazilian Portuguese',
  'pt-pt': 'European Portuguese',
  'pt_pt': 'European Portuguese',
  'european portuguese': 'European Portuguese',
  'es-es': 'European Spanish',
  'es_es': 'European Spanish',
  'european spanish': 'European Spanish',
  'es-mx': 'Latin American Spanish',
  'es_mx': 'Latin American Spanish',
  'es-419': 'Latin American Spanish',
  'es_419': 'Latin American Spanish',
  'latin american spanish': 'Latin American Spanish',
  'zh-cn': 'Simplified Chinese',
  'zh_cn': 'Simplified Chinese',
  'zh-hans': 'Simplified Chinese',
  'zh_hans': 'Simplified Chinese',
  'simplified chinese': 'Simplified Chinese',
  'zh-tw': 'Traditional Chinese',
  'zh_tw': 'Traditional Chinese',
  'zh-hant': 'Traditional Chinese',
  'zh_hant': 'Traditional Chinese',
  'traditional chinese': 'Traditional Chinese',
  'en-gb': 'British English',
  'en_gb': 'British English',
  'british english': 'British English',
  'cs-cz': 'Czech', 'cs_cz': 'Czech',
  'da-dk': 'Danish', 'da_dk': 'Danish',
  'bs-ba': 'Bosnian', 'bs_ba': 'Bosnian',
  'hr-hr': 'Croatian', 'hr_hr': 'Croatian',
  'fi-fi': 'Finnish', 'fi_fi': 'Finnish',
  'is-is': 'Icelandic', 'is_is': 'Icelandic',
  'bg-bg': 'Bulgarian', 'bg_bg': 'Bulgarian',
}

const normalizeLanguageIdentityValue = (value: unknown): string =>
  String(value || '').replace(/0/g, ' ').trim()

export const LANGUAGE_COUNTRY_CODES: Record<string, string> = {
  Dutch: 'NL',
  English: 'US',
  'British English': 'GB',
  French: 'FR',
  German: 'DE',
  Italian: 'IT',
  Portuguese: 'PT',
  'European Portuguese': 'PT',
  'Brazilian Portuguese': 'BR',
  Spanish: 'AR',
  'European Spanish': 'ES',
  'Latin American Spanish': 'MX',
  Japanese: 'JP',
  Korean: 'KR',
  Taiwanese: 'TW',
  'Traditional Chinese': 'TW',
  Chinese: 'CN',
  'Simplified Chinese': 'CN',
  Russian: 'RU',
  Polish: 'PL',
  Ukrainian: 'UA',
  Swedish: 'SE',
  Norwegian: 'NO',
  Turkish: 'TR',
  Serbian: 'RS',
  'South African': 'ZA',
  Afrikaans: 'ZA',
  Hebrew: 'HE',
  Thai: 'TH',
  Hindi: 'IN',
  Catalan: 'AD',
  Czech: 'CZ', Danish: 'DK', Bosnian: 'BA', Croatian: 'HR',
  Finnish: 'FI', Icelandic: 'IS', Bulgarian: 'BG',
}

const LANGUAGE_THEME_BY_COUNTRY: Record<string, {
  accent: string;
  background: string;
  border: string;
  text: string;
}> = {
  AD: { accent: '#d97706', background: '#fff7ed', border: '#fed7aa', text: '#7c2d12' },
  AR: { accent: '#0ea5e9', background: '#eff6ff', border: '#bfdbfe', text: '#1e3a8a' },
  BR: { accent: '#16a34a', background: '#f0fdf4', border: '#bbf7d0', text: '#14532d' },
  CN: { accent: '#dc2626', background: '#fef2f2', border: '#fecaca', text: '#7f1d1d' },
  DE: { accent: '#d97706', background: '#fffbeb', border: '#fde68a', text: '#78350f' },
  ES: { accent: '#dc2626', background: '#fef2f2', border: '#fecaca', text: '#7f1d1d' },
  FR: { accent: '#2563eb', background: '#eff6ff', border: '#bfdbfe', text: '#1e3a8a' },
  GB: { accent: '#7c3aed', background: '#f5f3ff', border: '#ddd6fe', text: '#4c1d95' },
  HE: { accent: '#475569', background: '#f8fafc', border: '#cbd5e1', text: '#334155' },
  IN: { accent: '#ea580c', background: '#fff7ed', border: '#fed7aa', text: '#7c2d12' },
  IT: { accent: '#16a34a', background: '#f0fdf4', border: '#bbf7d0', text: '#14532d' },
  JP: { accent: '#e11d48', background: '#fff1f2', border: '#fecdd3', text: '#881337' },
  KR: { accent: '#2563eb', background: '#eff6ff', border: '#bfdbfe', text: '#1e3a8a' },
  MX: { accent: '#15803d', background: '#f0fdf4', border: '#bbf7d0', text: '#14532d' },
  NL: { accent: '#ea580c', background: '#fff7ed', border: '#fed7aa', text: '#7c2d12' },
  NO: { accent: '#dc2626', background: '#fef2f2', border: '#fecaca', text: '#7f1d1d' },
  PL: { accent: '#e11d48', background: '#fff1f2', border: '#fecdd3', text: '#881337' },
  PT: { accent: '#047857', background: '#ecfdf5', border: '#a7f3d0', text: '#064e3b' },
  RS: { accent: '#2563eb', background: '#eff6ff', border: '#bfdbfe', text: '#1e3a8a' },
  RU: { accent: '#2563eb', background: '#eff6ff', border: '#bfdbfe', text: '#1e3a8a' },
  SE: { accent: '#ca8a04', background: '#fefce8', border: '#fde68a', text: '#713f12' },
  TH: { accent: '#be123c', background: '#fff1f2', border: '#fecdd3', text: '#881337' },
  TR: { accent: '#dc2626', background: '#fef2f2', border: '#fecaca', text: '#7f1d1d' },
  TW: { accent: '#0f766e', background: '#f0fdfa', border: '#99f6e4', text: '#134e4a' },
  UA: { accent: '#ca8a04', background: '#fefce8', border: '#fde68a', text: '#713f12' },
  US: { accent: '#2563eb', background: '#eff6ff', border: '#bfdbfe', text: '#1e3a8a' },
  ZA: { accent: '#16a34a', background: '#f0fdf4', border: '#bbf7d0', text: '#14532d' },
}

const ORIGINAL_LANGUAGE_VALUES = new Set(['original', 'proofread', 'original-proofread'])

export const SUPPORTED_TRANSLATION_FLAG_LANGUAGES = Object.keys(LANGUAGE_COUNTRY_CODES)

export const canonicalizeLanguageName = (value: unknown): string => {
  const normalized = normalizeLanguageIdentityValue(value)
  if (!normalized) return ''
  return LANGUAGE_NAME_ALIASES[normalized.toLowerCase()] || normalized
}

export const canonicalizeLanguageKey = (value: unknown): string =>
  canonicalizeLanguageName(value).toLowerCase()

export const languagesMatch = (left: unknown, right: unknown): boolean => {
  const leftKey = canonicalizeLanguageKey(left)
  const rightKey = canonicalizeLanguageKey(right)
  return Boolean(leftKey) && leftKey === rightKey
}

export const getLanguageCountryCode = (value: unknown): string | undefined => {
  const canonicalLanguage = canonicalizeLanguageName(value)
  return LANGUAGE_COUNTRY_CODES[canonicalLanguage]
}

export const countryCodeToFlagEmoji = (code: string): string => {
  if (!/^[A-Z]{2}$/i.test(code)) return ''
  return code
    .toUpperCase()
    .split('')
    .map(character => String.fromCodePoint(0x1F1E6 + character.charCodeAt(0) - 65))
    .join('')
}

export const getLanguageFlagFallback = (language: unknown, countryCode?: string): string => {
  const resolvedCountryCode = countryCode || getLanguageCountryCode(language)
  if (resolvedCountryCode === 'HE') return 'א'
  if (resolvedCountryCode) return countryCodeToFlagEmoji(resolvedCountryCode)
  const canonicalLanguage = canonicalizeLanguageName(language)
  return canonicalLanguage.slice(0, 2).toUpperCase()
}

export type LanguageVisualIdentity = {
  displayLanguage: string;
  countryCode?: string;
  flagFallback: string;
  badgeLabel: string;
  isOriginal: boolean;
  theme: {
    accent: string;
    background: string;
    border: string;
    text: string;
  };
}

export const resolveLanguageVisualIdentity = (
  value: unknown,
  options?: {
    label?: string;
    originalLabel?: string;
    sourceLanguage?: string;
  },
): LanguageVisualIdentity => {
  const normalized = normalizeLanguageIdentityValue(value)
  const lower = normalized.toLowerCase()
  const isOriginal = !normalized || ORIGINAL_LANGUAGE_VALUES.has(lower)
  const pairTargetLanguage = splitLanguagePair(normalized).targetLang
  const displayLanguage = isOriginal
    ? canonicalizeLanguageName(options?.sourceLanguage || options?.originalLabel || options?.label || 'Original')
    : canonicalizeLanguageName(pairTargetLanguage || options?.label || normalized)
  const countryCode = getLanguageCountryCode(displayLanguage)
  const theme = isOriginal
    ? {
        accent: 'var(--accent-primary)',
        background: 'var(--surface-muted)',
        border: 'var(--border-subtle)',
        text: 'var(--text-main)',
      }
    : LANGUAGE_THEME_BY_COUNTRY[countryCode || ''] || {
        accent: 'var(--accent-primary)',
        background: 'var(--surface-info)',
        border: 'color-mix(in srgb, var(--accent-primary) 34%, var(--border-subtle))',
        text: 'var(--text-main)',
      }
  return {
    displayLanguage,
    countryCode,
    flagFallback: getLanguageFlagFallback(displayLanguage, countryCode),
    badgeLabel: countryCode === 'HE' ? 'א' : displayLanguage.slice(0, 2).toUpperCase(),
    isOriginal,
    theme,
  }
}

const mergeTranslationProgressEntry = (
  current: TranslationProgressEntry | undefined,
  next: TranslationProgressEntry,
): TranslationProgressEntry => {
  if (!current) {
    return { ...next }
  }
  const translated = Math.max(current.translated_chapters || 0, next.translated_chapters || 0)
  const total = Math.max(current.total_chapters || 0, next.total_chapters || 0)
  const percentage = Math.max(current.percentage || 0, next.percentage || 0)
  let status: TranslationProgressEntry['status'] = 'not_started'
  if (current.status === 'complete' || next.status === 'complete') {
    status = 'complete'
  } else if (current.status === 'in_progress' || next.status === 'in_progress' || percentage > 0) {
    status = 'in_progress'
  }
  return {
    status,
    percentage,
    translated_chapters: translated,
    total_chapters: total,
  }
}

export const canonicalizeTranslationProgressMap = (
  progress?: TranslationProgressMap,
): TranslationProgressMap | undefined => {
  if (!progress) return undefined
  const entries = Object.entries(progress)
  if (!entries.length) return undefined
  const normalized: TranslationProgressMap = {}
  entries.forEach(([language, entry]) => {
    const canonicalLanguage = canonicalizeLanguageName(language)
    if (!canonicalLanguage) return
    normalized[canonicalLanguage] = mergeTranslationProgressEntry(
      normalized[canonicalLanguage],
      entry,
    )
  })
  return Object.keys(normalized).length ? normalized : undefined
}
